import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { MapPin } from "lucide-react";

function formatDate(d) {
  return new Date(d + "T00:00:00").toLocaleDateString("it-IT", { weekday: "short", day: "numeric", month: "long" });
}

function getResult(m) {
  if (m.home_score === null || m.home_score === undefined) return null;
  const isHome = m.home_team.toLowerCase().includes("artemide");
  const ours = isHome ? m.home_score : m.away_score;
  const theirs = isHome ? m.away_score : m.home_score;
  if (ours > theirs) return { label: "VITTORIA", cls: "bg-gold text-night" };
  if (ours < theirs) return { label: "SCONFITTA", cls: "bg-red-600 text-white" };
  return { label: "PAREGGIO", cls: "bg-zinc-600 text-white" };
}

function MatchCard({ m }) {
  const result = getResult(m);
  return (
    <article
      data-testid={`match-card-${m.id}`}
      className="grey-fade border border-line border-l-4 border-l-gold p-6 sm:p-8 flex flex-col lg:flex-row lg:items-center gap-6"
    >
      <div className="shrink-0 lg:w-44">
        <p className="font-mono text-xs tracking-[0.2em] uppercase text-gold">{m.competition}</p>
        <p className="font-display font-bold uppercase text-2xl text-white mt-1">
          {formatDate(m.date)}{m.time ? ` · ${m.time}` : ""}
        </p>
      </div>
      <div className="flex-1">
        <p className="font-display font-black uppercase text-3xl sm:text-4xl text-white leading-tight">
          {m.home_team}
          <span className="text-gold mx-3 text-2xl">VS</span>
          {m.away_team}
        </p>
        <p className="flex items-center gap-2 text-zinc-400 text-sm mt-2">
          <MapPin size={14} className="text-gold" /> {m.stadium}
        </p>
        {m.scorers && (
          <p className="font-mono text-xs tracking-[0.12em] uppercase text-gold mt-3" data-testid={`match-scorers-${m.id}`}>
            Gol · {m.scorers}
          </p>
        )}
      </div>
      <div className="shrink-0 flex items-center gap-4">
        <span className="bg-surface border border-line font-mono text-xs tracking-[0.2em] uppercase px-3 py-1 text-zinc-300">
          {m.squad}
        </span>
        {result ? (
          <>
            <span className="font-display font-black text-4xl sm:text-5xl text-white" data-testid={`match-score-${m.id}`}>
              {m.home_score} - {m.away_score}
            </span>
            <span className={`font-mono text-xs font-semibold tracking-[0.15em] uppercase px-3 py-1 ${result.cls}`}>
              {result.label}
            </span>
          </>
        ) : (
          <span className="font-mono text-xs font-semibold tracking-[0.15em] uppercase px-3 py-1 border border-gold text-gold">
            In Programma
          </span>
        )}
      </div>
    </article>
  );
}

const SECTIONS = [
  { squad: "FIGC", title: "Prima Squadra FIGC", sub: "Seconda Categoria · Girone O" },
  { squad: "CSI", title: "Seconda Squadra CSI", sub: "Open B · Girone B" },
];

export default function Calendario() {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("TUTTI");
  const [squad, setSquad] = useState("FIGC");

  useEffect(() => {
    api.get("/matches").then((r) => setMatches(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    return matches.filter((m) => {
      const played = m.home_score !== null && m.home_score !== undefined;
      if (status === "PROGRAMMATA" && played) return false;
      if (status === "GIOCATA" && !played) return false;
      return true;
    }).sort((a, b) => (status === "GIOCATA" ? b.date.localeCompare(a.date) : a.date.localeCompare(b.date)));
  }, [matches, status]);

  const Tab = ({ value, label, testid }) => (
    <button
      onClick={() => setStatus(value)}
      data-testid={testid}
      className={`font-display uppercase tracking-wider font-bold text-lg px-5 py-2 border-2 transition-colors ${
        status === value ? "bg-gold text-night border-gold" : "border-line text-zinc-300 hover:border-gold hover:text-gold"
      }`}
    >
      {label}
    </button>
  );

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 py-20" data-testid="calendario-page">
      <p className="font-mono text-xs sm:text-sm tracking-[0.3em] uppercase text-gold mb-4">Stagione 2026/27 · FIGC & CSI</p>
      <h1 className="font-display font-black uppercase text-6xl sm:text-8xl lg:text-9xl text-white leading-[0.85] tracking-[0.02em] mb-12" data-testid="calendario-title">
        Calen<span className="text-gold">dario</span>
      </h1>

      <div className="flex flex-wrap gap-3 mb-6" data-testid="calendario-squad-filters">
        {SECTIONS.map((s) => (
          <button
            key={s.squad}
            onClick={() => setSquad(s.squad)}
            data-testid={`filter-squad-${s.squad.toLowerCase()}`}
            className={`font-display uppercase tracking-wider font-bold text-xl px-6 py-3 border-2 transition-colors ${
              squad === s.squad ? "bg-gold text-night border-gold" : "border-line text-zinc-300 hover:border-gold hover:text-gold"
            }`}
          >
            {s.title}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-3 mb-12" data-testid="calendario-status-filters">
        <Tab value="TUTTI" label="Tutte" testid="filter-status-all" />
        <Tab value="PROGRAMMATA" label="In Programma" testid="filter-status-upcoming" />
        <Tab value="GIOCATA" label="Completate" testid="filter-status-played" />
      </div>

      {loading && <p className="text-zinc-400 font-mono text-sm" data-testid="calendario-loading">Caricamento partite...</p>}
      {!loading && filtered.length === 0 && (
        <p className="text-zinc-400 font-mono text-sm" data-testid="calendario-empty">Nessuna partita trovata per i filtri selezionati.</p>
      )}

      {!loading && filtered.filter((m) => m.squad === squad).length === 0 && (
        <p className="text-zinc-400 font-mono text-sm" data-testid="calendario-empty-squad">Nessuna partita per questa squadra con il filtro selezionato.</p>
      )}

      {SECTIONS.map((s) => {
        const list = filtered.filter((m) => m.squad === s.squad);
        if (s.squad !== squad || list.length === 0) return null;
        return (
          <section key={s.squad} className="mb-16" data-testid={`calendario-section-${s.squad.toLowerCase()}`}>
            <div className="flex items-center gap-4 mb-7">
              <span className="h-1 w-12 bg-gold" />
              <div>
                <h2 className="font-display font-black uppercase text-4xl sm:text-5xl text-white leading-none">{s.title}</h2>
                <p className="font-mono text-xs tracking-[0.25em] uppercase text-gold mt-2">{s.sub}</p>
              </div>
            </div>
            <div className="space-y-5">
              {list.map((m) => <MatchCard key={m.id} m={m} />)}
            </div>
          </section>
        );
      })}
    </main>
  );
}
